import {
	Breadcrumb,
	ChangeTriggerKind,
	ContextActionBar,
	FloatingActionButton,
	OptionsMenu,
	OptionsMenuItem,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction
} from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useFollowCategoryAction } from "../../Sidebar/useFollowCategoryAction";
import { HubItemCategoryModel, HubItemHubSidebarNodeModel } from "client/Api/Hub/hub";
import {
	ActiveFilter,
	getFollowCategorySubscriptionStatus,
	HubDialogContext,
	HubItemIsNewFilter
} from "../../common";
import { KnowledgeItemPreviewItemModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { CardOverview } from "../CardOverview/CardOverview";
import { Dashboard } from "client/Features/Hub/KnowledgeItems/Dashboard/Dashboard";
import { HubOverviewLayout, HubOverviewLayoutSectionContainerHandle } from "../../HubOverviewLayout";
import {
	MediaQueryKind,
	useForceRerender,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { TimeZone } from "client/DateAndTime/timezones";
import { useHubManagementDialog } from "../../Management/HubManagementDialog/HubManagementDialog";
import { CreateHubItemDialog } from "../../CreateHubItemDialog/CreateHubItemDialog";
import { isNotNil } from "client/Misc/utils";
import { defaultSortingOption, sortOptions } from "../common";
import { useHubItemsChangedListener } from "../../useHubItemsChangedListener";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import {
	KnowledgeItemBreadcrumbSegment,
	KnowledgeItemClickableBreadcrumbSegment,
	useKnowledgeItemBreadcrumb,
	getSegmentTrackingId
} from "../useKnowledgeItemBreadcrumb";
import { never } from "client_legacy/ndaw/errors";

export type CategoryOverviewProps = {
	hierarchy: readonly HubItemHubSidebarNodeModel[];
	activeFilter: ActiveFilter | null;
	onActiveFilterChange(filter: ActiveFilter | null): void;
	selectedCategoryId: number | null;
	onSelectedCategoryIdChange: (id: number) => void;
	categories: readonly HubItemCategoryModel[];
	getCategory: (id: number | nil) => HubItemCategoryModel | undefined;
	onCategoryCreated: () => void;
	onNavigateToItem(id: number): void;
	searchTerm: VersionedValue<string>;
	onSearchTermChange: (value: VersionedValue<string>) => void;
	sorting: DataSourceSortableState<KnowledgeItemPreviewItemModel>;
	onSortingChange: (value: DataSourceSortableState<KnowledgeItemPreviewItemModel>) => void;
	hasCreatePermission: boolean;
	canOpenHubManagement: boolean;
	onBreadcrumbNavigation: (segment: KnowledgeItemClickableBreadcrumbSegment) => void;
	isNewFilter: HubItemIsNewFilter;
	onIsNewFilterChange: (value: HubItemIsNewFilter) => void;
};

export const CategoryOverview = (props: CategoryOverviewProps): ReactElement => {
	const {
		hierarchy,
		activeFilter,
		onActiveFilterChange,
		selectedCategoryId,
		onSelectedCategoryIdChange,
		categories,
		getCategory,
		onCategoryCreated,
		onNavigateToItem,
		searchTerm,
		onSearchTermChange,
		sorting,
		onSortingChange,
		hasCreatePermission,
		onBreadcrumbNavigation,
		canOpenHubManagement,
		isNewFilter,
		onIsNewFilterChange
	} = props;

	const [shouldShowNewKnowledgeItemDialog, setShouldShowNewKnowledgeItemDialog] = useState<boolean>(false);

	const { setViewSpecificHandlers } = useContext(HubDialogContext);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();
	const [reloadData, dataVersion] = useForceRerender();
	const containerRef: RefObject<HubOverviewLayoutSectionContainerHandle | null> = useRef<HubOverviewLayoutSectionContainerHandle>(null);
	const [hubManagementDialog, openHubManagementDialog] = useHubManagementDialog();

	useHubItemsChangedListener(
		reloadData,
		HubItemKind.KnowledgeItem
	);

	const selectedCategory = useMemo(
		(): HubItemCategoryModel | undefined => getCategory(selectedCategoryId),
		[getCategory, selectedCategoryId]
	);

	const breadCrumb: readonly KnowledgeItemBreadcrumbSegment[] = useKnowledgeItemBreadcrumb({
		hierarchy,
		leaf: selectedCategory
			? {
				kind: "category",
				id: selectedCategory.id
			}
			: undefined
	});

	const preferredCategoryId: number | undefined = useMemo(
		() => {
			return categories
				.find(x => (x.id === selectedCategoryId && x.canCreateItem))
				?.id;
		},
		[categories, selectedCategoryId]
	);

	const isFollowing = useMemo(
		(): boolean => {
			if (categories.length === 0) {
				return false;
			}
			return selectedCategoryId === null
				? false
				: getFollowCategorySubscriptionStatus(categories, selectedCategoryId);
		},
		[categories, selectedCategoryId]
	);

	const closeNewKnowledgeItemDialogHandler = useCallback(
		(wasAnyCategoryCreated: boolean): void => {
			setShouldShowNewKnowledgeItemDialog(false);

			if (wasAnyCategoryCreated) {
				onCategoryCreated();
			}
		},
		[onCategoryCreated]
	);

	const scrollToTopHandler = useCallback(
		(): void => containerRef.current
			?.scrollToTop(),
		[]
	);

	const createNewHandler = useCallback(
		(): void => {
			setShouldShowNewKnowledgeItemDialog(true);
		},
		[]
	);

	const breadcrumbNavigationHandler = useCallback(
		(segment: KnowledgeItemBreadcrumbSegment): void => {
			switch(segment.kind) {
				case "item":
					throw new Error("Article is not clickable");
				case "category":
				case "group":
				case "overview":
					onBreadcrumbNavigation(segment);
					break;
				default:
					never(segment);
			}
		},
		[onBreadcrumbNavigation]
	);

	const followAction: ToolbarAction<KnowledgeItemActionContext> = useFollowCategoryAction<KnowledgeItemActionContext>(
		selectedCategoryId,
		isFollowing
	);

	const defaultActions = useMemo(
		(): readonly KnowledgeItemAction[] => ([
			{
				kind: ActionKind.Regular,
				name: "manage-knowledge-item",
				title: localize("_Manage"),
				icon: "gear",
				canExecute: (x): boolean => x.canOpenHubManagement,
				execute: () => {
					openHubManagementDialog({
						initialView: "knowledgeItem.overview"
					});
				}
			},
			followAction
		]),
		[
			followAction,
			openHubManagementDialog
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<KnowledgeItemActionContext> => {
			return getNewAction(createNewHandler);
		},
		[createNewHandler]
	);

	const topBarActions = useMemo(
		(): readonly KnowledgeItemAction[] => {
			return buildActionsForTopBar<KnowledgeItemActionContext, KnowledgeItemAction>(
				{
					defaultActions,
					mainActions: [mainAction]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive,
			mainAction
		]
	);

	const actionContext = useMemo(
		(): KnowledgeItemActionContext => ({
			activeFilter,
			hasCreatePermission,
			canOpenHubManagement,
			categoryId: selectedCategoryId
		}),
		[
			canOpenHubManagement,
			activeFilter,
			hasCreatePermission,
			selectedCategoryId
		]
	);

	useEffect(
		(): (() => void) => {
			const backHandler: (() => void) | undefined = activeFilter
				? () => onActiveFilterChange(null)
				: undefined;

			setViewSpecificHandlers({
				backHandler
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[
			setViewSpecificHandlers,
			activeFilter,
			onActiveFilterChange
		]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return () => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[
			actionContext,
			mainAction
		]
	);

	const shouldRenderDashboard: boolean = selectedCategoryId === null
		&& searchTerm.value.length === 0
		&& activeFilter === null;

	const hasFloatingButton: boolean = !activeFilter
		&& hasCreatePermission;

	useEffect(
		(): void => {
			if (shouldRenderDashboard) {
				onSortingChange(defaultSortingOption.value);
				onIsNewFilterChange("all");
			};
		},
		[
			onIsNewFilterChange,
			onSortingChange,
			shouldRenderDashboard
		]
	);

	const renderResult = (): ReactElement | null => {

		if (shouldRenderDashboard) {
			return (
				<Dashboard
					key={dataVersion}
					onCategoryClick={onSelectedCategoryIdChange}
					onNavigateToItem={onNavigateToItem}
					timezone={userTimezone}
				/>
			);
		}

		return (
			<CardOverview
				key={dataVersion}
				activeFilter={activeFilter}
				sorting={sorting}
				searchTerm={searchTerm.value}
				getCategory={getCategory}
				onNavigateToItem={onNavigateToItem}
				onActiveFilterChange={onActiveFilterChange}
				onSelectedCategoryIdChange={onSelectedCategoryIdChange}
				selectedCategoryId={selectedCategoryId}
				shouldReserveSpaceForFloatingActionButton={hasFloatingButton}
				onScrollToTop={scrollToTopHandler}
				timezone={userTimezone}
				isNewFilter={isNewFilter}
			/>
		);
	};

	return (
		<>
			<HubOverviewLayout trackingId="knowledge-items-overview">
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<HubOverviewLayout.Content>
					{isNotNil(selectedCategory) && (
						<HubOverviewLayout.Breadcrumb>
							<Breadcrumb
								segments={breadCrumb}
								onNavigate={breadcrumbNavigationHandler}
								trackingId="breadcrumb"
								getSegmentTrackingId={getSegmentTrackingId}
							/>
						</HubOverviewLayout.Breadcrumb>
					)}
					<HubOverviewLayout.Header
						trackingId="knowledge-items-category-overview-header"
						activeFilter={activeFilter}
						onClearActiveFilter={() => onActiveFilterChange(null)}
						title={selectedCategory?.name ?? localize("_AllCategories")}
						actions={topBarActions}
						actionContext={actionContext}
					>
						<SearchInputForm
							value={searchTerm}
							onChange={(value, version) => onSearchTermChange({ value, version })}
							placeholder={(
								<SearchPlaceholder
									kind={isNotNil(selectedCategoryId)
										? "filter"
										: "search"
									}
									properties={[
										localize("_Title")
									]}
								/>
							)}
							trackingId="search-input"
							trigger={{ kind: ChangeTriggerKind.Debounced }}
							height="large"
							icon={isNotNil(selectedCategoryId)
								? "filter"
								: "magnifying-glass"
							}
						/>


						{!shouldRenderDashboard && (
							<>
								<OptionsMenu
									title={localize("_FilterBy")}
									trackingId="filter-on"
									value={isNewFilter}
									onChange={onIsNewFilterChange}
									options={filterOptions}
									icon="filter"
									buttonSize="large"
								/>
								<OptionsMenu
									title={localize("_SortBy")}
									trackingId="sort-by"
									value={sorting}
									onChange={onSortingChange}
									options={sortOptions}
									icon="arrow-up-arrow-down"
									buttonSize="large"
								/>
							</>
						)}
					</HubOverviewLayout.Header>
					<HubOverviewLayout.SectionContainer
						trackingId="section"
						ref={containerRef}
					>
						{renderResult()}
						<AiAssistantFabSpacing />
					</HubOverviewLayout.SectionContainer>
					{fabHandler && (
						<FloatingActionButton
							icon="plus"
							onClick={fabHandler}
							trackingId="create-knowledge-item-mobile-button"
						/>
					)}
				</HubOverviewLayout.Content>
			</HubOverviewLayout>
			{shouldShowNewKnowledgeItemDialog && (
				<CreateHubItemDialog
					kind={HubItemKind.KnowledgeItem}
					preferredCategoryId={preferredCategoryId}
					onSaveHubItem={reloadData}
					onClose={closeNewKnowledgeItemDialogHandler}
					onBack={closeNewKnowledgeItemDialogHandler}
				/>
			)}
			{hubManagementDialog}
		</>
	);
};
CategoryOverview.displayName = "CategoryOverview";

type KnowledgeItemActionContext = {
	activeFilter: ActiveFilter | null;
	hasCreatePermission: boolean;
	canOpenHubManagement: boolean;
	categoryId: number | null;
};

type KnowledgeItemAction = ToolbarAction<KnowledgeItemActionContext>;

function getNewAction(
	handler: () => void
): ToolbarRegularAction<KnowledgeItemActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "create-knowledge-item",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (x): boolean => !x.activeFilter
			&& x.hasCreatePermission,
		execute: (): void => {
			handler();
		}
	};
}

export const filterOptions: readonly OptionsMenuItem<HubItemIsNewFilter>[] = [
	{
		value: "all",
		text: localize("_All"),
		valueTrackingId: "all-announcements"
	},
	{
		value: "new",
		text: localize("_New"),
		valueTrackingId: "new-announcements"
	}
];